import type { CmsProductCarouselItem } from '@johnlewispartnership/wtr-content-component-library';

import type { PurchaseOrAmendType } from 'renderer/hooks/useTrackVirtualPageView';

// ToDo: WPIP-83492 - remove once CCL types include analytics fields
type SearchProductAnalyticsFields = {
  brand?: string;
  categories?: { id: string; name: string; urlName?: string }[];
  promotion?: {
    promotionId?: number;
    promotionUnitPrice?: { amount: number };
  };
  promotions?: { promotionId: number; promotionDescription: string }[];
  currentSaleUnitPrice?: { price: { amount?: number; currencyCode?: string } };
  availability?: string;
};

export type CarouselItem = CmsProductCarouselItem & {
  searchProduct: SearchProductAnalyticsFields;
};

export type ListContext = {
  item_list_name?: string;
  item_list_id?: string;
  item_list_title?: string;
  product_finding_method?: string;
  purchase_or_amend: PurchaseOrAmendType;
};

type FavouritesEvent = 'add_to_wishlist' | 'remove_from_wishlist';
type TrolleyEvent = 'add_to_cart' | 'remove_from_cart';
type ItemListEvent = 'select_item' | 'view_item_list';
type AnalyticsEvent = FavouritesEvent | TrolleyEvent | ItemListEvent;

const PRODUCT_POD_VARIANT = 'slim';
const PFM_TABLE_VALUE = 'Content';

const getIsFavoriteStatus = (event: AnalyticsEvent, product: CarouselItem['searchProduct']) => {
  if (event === 'add_to_wishlist') return 'YES';
  if (event === 'remove_from_wishlist') return 'NO';

  return product?.favourite ? 'YES' : 'NO';
};

export const buildItem = (
  item: CarouselItem,
  item_index: number,
  event: AnalyticsEvent,
  listContext?: ListContext,
  quantity?: number,
) => {
  const product = item.searchProduct;
  const categoryEntries = Object.fromEntries(
    (product.categories ?? []).map((category, index) => [
      index === 0 ? 'item_category' : `item_category${index + 1}`,
      category.name,
    ]),
  );
  const regularPrice = product.currentSaleUnitPrice?.price.amount;
  const promoPrice = product.promotion?.promotionUnitPrice?.amount;
  const discountValue =
    regularPrice != null && promoPrice != null ? Number((regularPrice - promoPrice).toFixed(2)) : 0;

  return {
    item_id: product.lineNumber,
    item_name: product.name,
    item_brand: product.brand ?? 'unknown',
    item_variant: product.productType ?? 'unknown',
    ...categoryEntries,
    price: regularPrice ?? 'unknown',
    quantity: quantity ?? product.defaultQuantity?.amount ?? 1,
    discount: discountValue,
    index: item_index,

    // WR Custom Dimensions & Custom Fields
    availability: product.availability ?? 'unknown',
    pod_type: PRODUCT_POD_VARIANT,
    offer: product.promotions?.[0]?.promotionDescription ?? 'unknown',
    offer_id: product.promotions?.[0]?.promotionId ?? 'unknown',
    product_sku: product.id ?? 'unknown',
    uom: product.defaultQuantity?.uom ?? 'unknown',
    sponsored: product.sponsored ? 'YES' : 'NO',
    sponsored_source: 'unknown',
    favourite: getIsFavoriteStatus(event, product),
    ...listContext,
    product_finding_method: listContext?.item_list_name ?? 'unknown',
  };
};

export const buildEvent = (
  event: AnalyticsEvent,
  item: CarouselItem,
  item_index: number,
  listContext?: ListContext,
  quantity?: number,
) => ({
  event,
  ecommerce: {
    currencyCode: item.searchProduct.currentSaleUnitPrice?.price.currencyCode ?? 'GBP',
    value: item.searchProduct.currentSaleUnitPrice?.price.amount,
    ...listContext,
    items: [buildItem(item, item_index, event, listContext, quantity)],
  },
});

// Mapping confirmed by the Analytics Team
const PRODUCT_CURATION_BY_SOURCE: Record<string, string> = {
  product_collection: 'Manually Curated',
  offer_ids: 'Promotions',
  my_favourites: 'Manually Curated',
  my_favourites_offer: 'Manually Curated',
  my_last_order: 'Purchase History',
  personalised_feed: 'Personalised',
};

export const buildListContext = (
  purchaseOrAmend: PurchaseOrAmendType,
  productSource?: string,
  title?: string,
): ListContext => ({
  item_list_name: PFM_TABLE_VALUE,
  item_list_id: `Carousel_${PRODUCT_CURATION_BY_SOURCE[productSource ?? ''] ?? 'Manually Curated'}_${PFM_TABLE_VALUE}`,
  item_list_title: title ?? 'unknown',
  purchase_or_amend: purchaseOrAmend,
});

export const findItem = (items: CarouselItem[], lineNumber: string) => {
  const itemIndex = items.findIndex(item => item.searchProduct.lineNumber === lineNumber);
  if (itemIndex === -1) {
    return undefined;
  }

  return {
    index: itemIndex + 1,
    item: items[itemIndex],
  };
};
